import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useSearchParams, Navigate } from 'react-router-dom';
import PasswordGate from './components/PasswordGate';
import ReportViewer from './components/ReportViewer';
import type { ReportData } from './types';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorDisplay from './components/ErrorDisplay';

/**
 * Share route component that handles the /bbv/:ranid route
 */
const ShareRoute: React.FC = () => {
  const { ranid } = useParams<{ ranid: string }>();
  const [searchParams] = useSearchParams();
  const lnkuuid = searchParams.get('lnkuuid');
  // Get viewer type from URL params (vt=D for doctor, vt=P for parent, etc.)
  const viewerTypeParam = searchParams.get('vt') || searchParams.get('viewerType');

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [shareId, setShareId] = useState<string | null>(null);
  const [viewerType, setViewerType] = useState<string | null>(viewerTypeParam);
  const [accessCode, setAccessCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check if we have valid parameters
  useEffect(() => {
    if (!ranid || !lnkuuid) {
      setError('Invalid share link. Please use the link from your email.');
    }
  }, [ranid, lnkuuid]);

  // Always require passcode on reload: clear any prior session tokens on mount
  useEffect(() => {
    try {
      sessionStorage.removeItem('shareToken');
      if (ranid && lnkuuid) {
        sessionStorage.removeItem(`share_${ranid}_${lnkuuid}`);
      }
    } catch { }
  }, [ranid, lnkuuid]);

  const handleAuthenticated = (validatedShareId: string, validatedViewerType?: string, validatedAccessCode?: string) => {
    setShareId(validatedShareId);
    setIsAuthenticated(true);
    setAccessCode(validatedAccessCode || null);
    if (validatedViewerType) {
      setViewerType(validatedViewerType);
    }

    // Store the share association
    if (ranid && lnkuuid) {
      sessionStorage.setItem(`share_${ranid}_${lnkuuid}`, validatedShareId);
      if (validatedViewerType) {
        sessionStorage.setItem(`share_viewerType_${ranid}_${lnkuuid}`, validatedViewerType);
      }
    }
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    // Clear error after 5 seconds
    setTimeout(() => setError(null), 5000);
  };

  // Show error if invalid parameters
  if (!ranid || !lnkuuid) {
    return <ErrorDisplay error={error || 'Invalid share link'} fullScreen />;
  }

  // Show password gate if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        {error && (
          <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg z-50">
            <p>{error}</p>
          </div>
        )}
        <PasswordGate
          randomId={ranid}
          linkUuid={lnkuuid}
          viewerType={viewerType || undefined}
          onValidated={handleAuthenticated}
          onError={handleError}
        />
      </>
    );
  }

  // Show report viewer if authenticated
  if (shareId) {
    return <ReportViewer shareId={shareId} viewerType={viewerType || undefined} accessCode={accessCode || undefined} />;
  }

  // Fallback loading state
  return <LoadingSpinner message="Initializing..." fullScreen />;
};

/**
 * Main App component
 *  Psikosen's   - constructing a secure access system
 */
const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Main share route */}
        <Route path="/bbv/:ranid" element={<ShareRoute />} />
        {/* Mock demo route for local preview */}
        <Route path="/demo" element={<DemoPage />} />
        {/* Demo route under /bbv for production hosting */}
        <Route path="/bbv/demo" element={<DemoPage />} />

        {/* Redirect root to a help page */}
        <Route path="/" element={
          <div className="min-h-screen flex items-center justify-center px-4 py-10">
            <div className="report-section max-w-2xl text-center">
              <p className="report-eyebrow">Secure Access</p>
              <div className="mt-4 flex flex-col items-center">
                <img
                  src="/asset/mamabear_black.png"
                  alt="MamaBear Health"
                  className="h-44 w-auto"
                />
                <img
                  src="/asset/mama_origin.png"
                  alt=""
                  className="h-24 w-24 -mt-8 rounded-full object-cover border border-[var(--border)] bg-white"
                />
              </div>
              <h1 className="report-card__title mt-6">
                MamaBear Health Report Viewer
              </h1>
              <p className="report-card__subtitle mx-auto max-w-xl">
                Use the secure link from your email to open a shared report, review the on-page clinical summary, and compare any attached follow-up videos.
              </p>
              <p className="mt-6 text-sm text-[var(--text-secondary)]">
                Need help?{' '}
                <a
                  href="mailto:mamabear@loonmd.com"
                  className="report-copy-link"
                >
                  mamabear@loonmd.com
                </a>
              </p>
            </div>
          </div>
        } />

        {/* Catch all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <SiteFooter />
    </Router>
  );
};

export default App;

// Simple demo page with mock data to preview the layout
const DemoPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const viewerType = searchParams.get('vt') || searchParams.get('viewerType') || undefined;
  const demoAccessCode = searchParams.get('code') || '482913';
  const singleReportPreview = searchParams.get('single') === '1';
  // Build an absolute base for assets so the video resolver doesn't prepend /api
  const assetHost =
    typeof window !== 'undefined' &&
      (window.location.hostname === 'viewer.mamabearhealthapp.com' ||
        window.location.hostname === 'www.viewer.mamabearhealthapp.com')
      ? 'https://mamabearhealthapp.com'
      : (typeof window !== 'undefined' ? window.location.origin : '');
  const demoVideoBase = 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4';

  const mock: ReportData = {
    shareId: 'demo-share',
    childInfo: {
      name: 'JOHNSON, MARIE A.',
      age: '4y 6m',
      gender: 'FEMALE',
      dateOfBirth: '2019-06-01',
      insuranceStatus: 'Private or employer-based insurance',
      primaryCareProvider: 'Dr. Marie John, MD',
      primaryCarePractice: 'Bay Pediatrics',
      primaryCarePhone: '(415) 555-0142',
      healthHistory: [
        { key: 'hist_IMM_UTD', condition: 'Up to date', value: 'True' },
        { key: 'hist_PHA', condition: 'Parental history of asthma', value: 'True' },
        { key: 'hist_SA', condition: 'Suspected asthma', value: 'True' },
        { key: 'hist_ESP', condition: 'Exposed to smoke during pregnancy', value: 'True' },
        { key: 'hist_CE', condition: 'Currently exposed to smoke', value: 'True' },
        { key: 'hist_ALPN', condition: 'Peanut Allergy', value: 'True' },
        { key: 'hist_ALMM', condition: 'Mold/Mildew Allergy', value: 'True' },
        { key: 'hist_PC', condition: 'Prior croup', value: 'True' },
        { key: 'hist_EC', condition: 'Eczema', value: 'True' },
        { key: 'hist_PB', condition: 'Prior bronchitis', value: 'True' },
        { key: 'hist_AL', condition: 'Allergies', value: 'True' },
        { key: 'hist_PHRI', condition: 'Prior hospitalizations due to respiratory illness', value: 'True' },
        { key: 'hist_ALEG', condition: 'Egg Allergy', value: 'True' },
        { key: 'hist_ALMI', condition: 'Milk Allergy', value: 'True' },
        { key: 'hist_PP', condition: 'Prior pneumonia', value: 'True' },
        { key: 'hist_WH4M', condition: 'Wheezing 4 or more times', value: 'True' },
        { key: 'hist_ALSE', condition: 'Seasonal (pollen) Allergy', value: 'True' },
        { key: 'hist_WUC', condition: 'Wheezing unrelated to colds', value: 'True' },
        { key: 'hist_ALPET', condition: 'Pet Allergy', value: 'True' },
        { key: 'hist_POST', condition: 'Post-Term: > 42 weeks', value: 'True' },
        { key: 'meds_AL', condition: 'Albuterol', value: 'True' },
      ],
    },
    parentInfo: {
      name: 'Parent Name',
      email: 'parent@example.com',
      phone: '555-555-5555',
      location: '55416',
      communityType: 'Suburban',
    },
    clinicalFinding: {
      id: 'demo-clinical-finding',
      notes: 'Reviewed parent-submitted symptoms and video. Findings are consistent with mild increased work of breathing without visible cyanosis.',
      createdAt: '2026-04-14T14:05:00.000Z',
      updatedAt: '2026-04-14T14:05:00.000Z',
    },
    eCheckChainId: 'demo-chain-001',
    chainData: [
      {
        id: 'ci-1',
        eCheckChain: 'demo-chain-001',
        date: '2026-05-18T14:15:00-04:00',
        dateDisplayServer: 'May 18, 2026 at 2:15 PM',
        levelOfConcern: 7,
        parentConcernText: 'Her breathing got really fast at lunch. She vomited and is wheezing. Took her to the ER.',
        hasActiveSymptoms: true,
        alreadyGivenMedication: true,
        providerAskedForFollowUp: false,
        seenByProvider: true,
        seenByProviderLocation: 'Emergency',
        childWasSeenIn: 'Emergency',
        alertLevel: 'warn' as const,
        alertMessage: 'Multiple moderate symptoms. Oxygen, nebulizer, steroids given at ED. Elevated mAPI risk. Review video.',
        medicationStatus: 'already_given' as const,
        medicationStatusLabel: 'Medications already given — symptoms reflect post-medication state',
        activityOnset: 'Eating — 15 min or less',
        activityLevel: 'Slightly decreased',
        symptomDuration: '2 days',
        symptomHistory: ['sym1_CO', 'sym1_NB'],
        symptomHistoryLabels: ['Cough', 'Noisy Breathing'],
        isRepetitive: 'Yes',
        sickContacts: 'Yes — daycare',
        drinkingStatus: 'Drinking less than usual',
        normalUrination: true,
        poopStatus: 'Normal for my child',
        dehydrationSigns: [],
        sleepQuality: ['Not normal', 'Restless'],
        diagnoses: ['RSV Bronchiolitis'],
        treatments: ['Oxygen therapy', 'Nebulizer', 'Steroids'],
        followUpRequired: false,
        followUpMedications: [],
        followUpMedicationNotes: null,
        symptoms: [
          { type: 'RapidBreathing', severity: 'Moderate', severityCode: 1, duration: '' },
          { type: 'NoisyBreathing', severity: 'Moderate', severityCode: 1, duration: '' },
          { type: 'ChestRetractions', severity: 'Moderate', severityCode: 1, duration: '' },
          { type: 'NasalCongestion', severity: 'Mild', severityCode: 0, duration: '' },
          // Mirrors the API's numeric default so the report never mistakes a non-fever 0 for 0°F.
          { type: 'Cough', severity: 'Moderate', severityCode: 1, duration: '', temperatureValue: 0, temperatureUnit: 'F' },
          { type: 'RapidHeartRate', severity: 'Mild', severityCode: 0, duration: '' },
          { type: 'VomitingDiarrhea', severity: 'Mild', severityCode: 0, duration: '' },
          { type: 'FeverAxillary', severity: 'Mild', severityCode: 0, duration: '', temperatureValue: 100.8, temperatureUnit: 'F' },
        ],
        medications: [{ name: 'Albuterol (nebulizer)', dosage: '', time: '2:15 PM' }, { name: 'Oral steroids', dosage: '', time: '2:15 PM' }],
        exposures: [{ type: 'Flu / Cold / RSV' }, { type: 'Pet dander' }],
        activities: [{ type: 'Eating', impact: '15 min or less' }],
      },
      {
        id: 'ci-2',
        eCheckChain: 'demo-chain-001',
        date: '2026-05-18T14:31:00-04:00',
        dateDisplayServer: 'May 18, 2026 at 2:31 PM',
        levelOfConcern: 7,
        parentConcernText: 'Still watching her breathing. It does not look better yet.',
        hasActiveSymptoms: true,
        alreadyGivenMedication: true,
        providerAskedForFollowUp: true,
        seenByProvider: true,
        seenByProviderLocation: 'Emergency',
        childWasSeenIn: 'Emergency',
        followUpReason: 'Reassess breathing after the emergency-department treatment.',
        symptomImprovement: 'No Change',
        symptomImprovementCode: 0,
        alertLevel: 'warn' as const,
        alertMessage: 'No change at 15 min. Wet diapers reduced. Retractions still present.',
        medicationStatus: 'already_given' as const,
        medicationStatusLabel: 'Medications already given — symptoms reflect post-medication state',
        activityOnset: 'Eating — 15 min or less',
        activityLevel: 'Slightly decreased',
        symptomDuration: '2 days',
        isRepetitive: 'Yes',
        sickContacts: 'Yes — daycare',
        drinkingStatus: 'Drinking less than usual',
        normalUrination: false,
        dehydrationSigns: ['Fewer wet diapers than usual'],
        sleepQuality: ['Not normal', 'Restless'],
        diagnoses: ['RSV Bronchiolitis'],
        treatments: ['Nebulizer', 'Steroids'],
        followUpRequired: false,
        followUpMedications: [],
        followUpMedicationNotes: null,
        symptoms: [
          { type: 'RapidBreathing', severity: 'Moderate', severityCode: 1, duration: '', improvement: 'No change', symptomChange: 'No change', symptomChangeCode: 0 },
          { type: 'NoisyBreathing', severity: 'Moderate', severityCode: 1, duration: '', improvement: 'No change', symptomChange: 'No change', symptomChangeCode: 0 },
          { type: 'ChestRetractions', severity: 'Moderate', severityCode: 1, duration: '', improvement: 'No change', symptomChange: 'No change', symptomChangeCode: 0 },
          { type: 'Cough', severity: 'Moderate', severityCode: 1, duration: '', improvement: 'No change', symptomChange: 'No change', symptomChangeCode: 0 },
          { type: 'Fever', severity: 'Mild', severityCode: 0, duration: '', temperatureValue: 100.6, temperatureUnit: 'F', improvement: 'No change', symptomChange: 'No change', symptomChangeCode: 0 },
        ],
        medications: [{ name: 'Albuterol (nebulizer)', dosage: '', time: '2:31 PM' }],
        exposures: [{ type: 'Flu / Cold / RSV' }],
        activities: [],
      },
      {
        id: 'ci-3',
        eCheckChain: 'demo-chain-001',
        date: '2026-05-18T14:47:00-04:00',
        dateDisplayServer: 'May 18, 2026 at 2:47 PM',
        levelOfConcern: 6,
        parentConcernText: 'Breathing looks a little easier. She seems calmer.',
        hasActiveSymptoms: true,
        alreadyGivenMedication: true,
        seenByProvider: true,
        seenByProviderLocation: 'Emergency',
        childWasSeenIn: 'Emergency',
        alertLevel: 'info' as const,
        alertMessage: 'Partial early response at 30 min. Breathing slightly easier. Fever unchanged.',
        symptomImprovement: 'Little Better',
        symptomImprovementCode: 2,
        medicationStatus: 'already_given' as const,
        medicationStatusLabel: 'Medications already given — symptoms reflect post-medication state',
        activityLevel: 'Slightly decreased',
        symptomDuration: '2 days',
        isRepetitive: 'Yes',
        sickContacts: 'Yes — daycare',
        drinkingStatus: 'Drinking less than usual',
        normalUrination: true,
        dehydrationSigns: [],
        sleepQuality: ['Not normal', 'Restless'],
        diagnoses: ['RSV Bronchiolitis'],
        treatments: ['Nebulizer', 'Steroids'],
        followUpRequired: false,
        followUpMedications: [],
        followUpMedicationNotes: null,
        symptoms: [
          { type: 'RapidBreathing', severity: 'Mild', severityCode: 0, duration: '', improvement: 'A little better', symptomChange: 'Little Better', symptomChangeCode: 2 },
          { type: 'NoisyBreathing', severity: 'Mild', severityCode: 0, duration: '', improvement: 'A little better', symptomChange: 'Little Better', symptomChangeCode: 2 },
          { type: 'ChestRetractions', severity: 'Mild', severityCode: 0, duration: '', improvement: 'A little better', symptomChange: 'Little Better', symptomChangeCode: 2 },
          { type: 'Cough', severity: 'Moderate', severityCode: 1, duration: '', improvement: 'No change', symptomChange: 'No Change', symptomChangeCode: 0 },
          { type: 'Fever', severity: '', severityCode: null, duration: '', temperatureValue: 100.5, temperatureUnit: 'F', improvement: 'No change', symptomChange: 'No Change', symptomChangeCode: 0 },
        ],
        medications: [{ name: 'Albuterol (nebulizer)', dosage: '', time: '2:47 PM' }],
        exposures: [{ type: 'Flu / Cold / RSV' }],
        activities: [],
      },
      {
        id: 'ci-4',
        eCheckChain: 'demo-chain-001',
        date: '2026-05-19T08:30:00-04:00',
        dateDisplayServer: 'May 19, 2026 at 8:30 AM',
        levelOfConcern: 6,
        parentConcernText: 'She had a really rough night. Coughing all night and would not sleep flat. Pulling at her left ear.',
        hasActiveSymptoms: true,
        alreadyGivenMedication: false,
        seenByProvider: false,
        childWasSeenIn: 'No',
        alertLevel: 'warn' as const,
        alertMessage: 'New: ear pain. Fewer wet diapers. Cough and congestion worsening. Cannot sleep lying flat.',
        symptomImprovement: 'Getting Worse',
        symptomImprovementCode: 1,
        medicationStatus: 'will_give' as const,
        medicationStatusLabel: 'Parent plans to give medications — symptoms may reflect pre-medication state',
        activityLevel: 'Significantly decreased',
        symptomDuration: '3 days',
        isRepetitive: 'Yes',
        sickContacts: 'Yes — daycare',
        drinkingStatus: 'Drinking less than usual',
        normalUrination: false,
        dehydrationSigns: ['No tears when crying'],
        sleepQuality: ['Not normal', 'Coughs overnight', 'Cannot sleep lying flat', 'Restless', 'Frequent waking'],
        diagnoses: ['RSV Bronchiolitis'],
        treatments: [],
        followUpRequired: false,
        followUpMedications: [],
        followUpMedicationNotes: null,
        symptoms: [
          { type: 'NasalCongestion', severity: '', severityCode: null, duration: '', improvement: 'Getting worse', symptomChange: 'Getting Worse', symptomChangeCode: 1 },
          { type: 'Cough', severity: '', severityCode: null, duration: '', improvement: 'Getting worse', symptomChange: 'Getting Worse', symptomChangeCode: 1 },
          { type: 'EarPain', severity: '', severityCode: null, duration: '', improvement: 'No change', symptomChange: 'No Change', symptomChangeCode: 0 },
          { type: 'RapidBreathing', severity: '', severityCode: null, duration: '', improvement: 'A little better', symptomChange: 'Little Better', symptomChangeCode: 2 },
        ],
        medications: [{ name: 'Albuterol (inhaler)', dosage: '', time: '8:30 AM' }, { name: 'Acetaminophen', dosage: '', time: '8:30 AM' }],
        exposures: [{ type: 'Flu / Cold / RSV' }],
        activities: [],
      },
      {
        id: 'ci-5',
        eCheckChain: 'demo-chain-001',
        date: '2026-05-20T06:45:00-04:00',
        dateDisplayServer: 'May 20, 2026 at 6:45 AM',
        levelOfConcern: 8,
        parentConcernText: 'She got worse overnight. Breathing faster and harder. I gave her albuterol at 3 AM.',
        hasActiveSymptoms: true,
        alreadyGivenMedication: true,
        seenByProvider: false,
        childWasSeenIn: 'No',
        alertLevel: 'warn' as const,
        alertMessage: 'Worsening. Lethargic. Refusing most fluids. Dehydration signs. Cough severe. Urgent review recommended.',
        medicationStatus: 'already_given' as const,
        medicationStatusLabel: 'Albuterol given at 3 AM — symptoms reflect post-medication state',
        activityLevel: 'Lethargic',
        symptomDuration: '4 days',
        isRepetitive: 'Yes',
        sickContacts: 'Yes — daycare',
        drinkingStatus: 'Refusing most fluids',
        normalUrination: false,
        dehydrationSigns: ['No tears when crying', 'Very dry mouth / lips'],
        sleepQuality: ['Not normal', 'Coughs overnight', 'Cannot sleep lying flat', 'Restless', 'Frequent waking', 'Difficulty staying asleep'],
        diagnoses: ['RSV Bronchiolitis'],
        treatments: [],
        followUpRequired: false,
        followUpMedications: [],
        followUpMedicationNotes: null,
        symptoms: [
          { type: 'Cough', severity: 'Severe', severityCode: 3, duration: '', improvement: 'Getting worse' },
          { type: 'RapidBreathing', severity: 'Moderate', severityCode: 2, duration: '', improvement: 'Getting worse' },
          { type: 'NoisyBreathing', severity: 'Moderate', severityCode: 2, duration: '', improvement: 'Getting worse' },
          { type: 'EarPain', severity: 'Moderate', severityCode: 2, duration: '', improvement: 'Getting worse' },
          { type: 'Fever', severity: 'Mild', severityCode: 1, duration: '', temperatureValue: 99.1, temperatureUnit: 'F' },
        ],
        medications: [{ name: 'Albuterol (inhaler)', dosage: '', time: '3:00 AM' }],
        exposures: [{ type: 'Flu / Cold / RSV' }],
        activities: [],
      },
      {
        id: 'ci-6',
        eCheckChain: 'demo-chain-001',
        date: '2026-05-22T09:15:00-04:00',
        dateDisplayServer: 'May 22, 2026 at 9:15 AM',
        levelOfConcern: 4,
        parentConcernText: 'She is much better today! Playing a little. Still has ear pain and cough but breathing is way better.',
        hasActiveSymptoms: true,
        alreadyGivenMedication: true,
        seenByProvider: true,
        seenByProviderLocation: 'Clinic',
        childWasSeenIn: 'Clinic',
        alertLevel: 'info' as const,
        alertMessage: 'Clear improvement. Clinic visit confirmed OM. Antibiotics started. Retractions resolved.',
        medicationStatus: 'already_given' as const,
        medicationStatusLabel: 'Antibiotics started Day 4. Albuterol as needed.',
        activityLevel: 'Slightly decreased',
        symptomDuration: '5 days',
        isRepetitive: 'Yes',
        sickContacts: 'Yes — daycare',
        drinkingStatus: 'Drinking well',
        normalUrination: true,
        dehydrationSigns: [],
        sleepQuality: ['Not normal', 'Restless', 'Frequent waking'],
        diagnoses: ['RSV Bronchiolitis', 'Acute otitis media'],
        treatments: ['Antibiotics'],
        followUpRequired: true,
        followUpMedications: [],
        followUpMedicationNotes: null,
        symptoms: [
          { type: 'RapidBreathing', severity: 'Trace', severityCode: 1, duration: '', improvement: 'Much better' },
          { type: 'Cough', severity: 'Mild', severityCode: 1, duration: '', improvement: 'Much better' },
          { type: 'EarPain', severity: 'Mild', severityCode: 1, duration: '', improvement: 'A little better' },
          { type: 'NasalCongestion', severity: 'Mild', severityCode: 1, duration: '', improvement: 'A little better' },
        ],
        medications: [{ name: 'Amoxicillin', dosage: '', time: '9:00 AM' }, { name: 'Albuterol (inhaler)', dosage: '', time: '9:00 AM' }],
        exposures: [{ type: 'Flu / Cold / RSV' }],
        activities: [{ type: 'Playing indoors', impact: 'Slightly reduced' }],
      },
      {
        id: 'ci-7',
        eCheckChain: 'demo-chain-001',
        date: '2026-05-31T18:20:00-04:00',
        dateDisplayServer: 'May 31, 2026 at 6:20 PM',
        levelOfConcern: 7,
        parentConcernText: "She's thrown up four times since lunch and won't keep water down. Her lips look dry and she is very floppy.",
        hasActiveSymptoms: true,
        alreadyGivenMedication: false,
        seenByProvider: true,
        seenByProviderLocation: 'Clinic',
        childWasSeenIn: 'Clinic',
        providerAskedForFollowUp: true,
        followUpProviderName: 'Dr. Marie John, MD',
        alertLevel: 'warn' as const,
        alertMessage: 'New illness episode with fever, repeated vomiting, poor fluid intake, and dehydration signs.',
        medicationStatus: 'will_give' as const,
        medicationStatusLabel: 'No medication given yet — symptoms reflect pre-medication state',
        activityLevel: 'Lethargic',
        activityOnset: 'Sleeping — nap',
        symptomDuration: 'Less than a day',
        isRepetitive: 'No',
        sickContacts: 'Someone in the household or close to the child',
        drinkingStatus: 'Refusing most fluids',
        diaperStatus: 'Fewer wet diapers/urine than usual',
        poopStatus: 'Diarrhea/looser than usual',
        normalUrination: false,
        dehydrationSigns: ['Very dry mouth/lips'],
        sleepQuality: ['Not sleeping normally', 'Restless', 'Frequently waking'],
        diagnoses: ['Viral illness under evaluation'],
        treatments: [],
        followUpRequired: true,
        followUpReason: 'Reassess fever, vomiting, and hydration after the clinic visit.',
        followUpMedications: [],
        followUpMedicationNotes: null,
        symptoms: [
          { type: 'FeverOrally', severity: 'Moderate', severityCode: 2, duration: '', temperatureValue: 102.3, temperatureUnit: 'F' },
          { type: 'SoreThroat', severity: 'Mild', severityCode: 1, duration: '' },
          { type: 'VomitingDiarrhea', severity: 'Severe', severityCode: 3, duration: '' },
        ],
        medications: [],
        exposures: [{ type: 'Stomach virus' }, { type: 'Sick contacts', description: 'Someone in the household or close to the child' }],
        activities: [{ type: 'Sleeping', impact: 'Nap' }],
      },
    ],
    checkInData: {
      id: 'demo',
      date: new Date().toISOString(),
      dateDisplayServer: 'April 14, 2026 at 9:56 AM',
      hasActiveSymptoms: true,
      alreadyGivenMedication: true,
      providerAskedForFollowUp: true,
      childWasSeenIn: 'Telemedicine',
      symptomDuration: '3 days',
      isRepetitive: 'No',
      activityLevel: 'Slightly decreased',
      hydrationLevel: 'Drinking well',
      sleepQuality: ['Restless', 'Coughing at night'],
      drinkingStatus: 'Drinking less than usual',
      diaperStatus: 'Normal (wet diaper/urine within last 8 hours)',
      poopStatus: 'Normal for my child',
      dehydrationSigns: [],
      normalUrination: true,
      diagnoses: ['Cold/Upper respiratory infection'],
      treatments: ['Nebulizer', 'Fever reducers'],
      followUpRequired: true,
      followUpReason: 'Requested to reassess cough and breathing after medication.',
      followUpMedications: [],
      followUpMedicationNotes: null,
      levelOfConcern: 7,
      parentConcernText: 'Her breathing got really fast at lunch. She vomited and is wheezing. Took her to the ER.',
      alertLevel: 'warn' as const,
      alertMessage: 'Multiple moderate symptoms. Oxygen, nebulizer, steroids given at ED. Elevated mAPI risk. Review video.',
      medicationStatus: 'already_given' as const,
      medicationStatusLabel: 'Medications already given — symptoms reflect post-medication state',
      seenByProvider: true,
      seenByProviderLocation: 'Emergency',
      activityOnset: 'Eating — 15 min or less',
      sickContacts: 'Yes — daycare',
      symptomComments: 'Level of concern: 6/10, Parent reports a dry cough, faster breathing, and congestion that worsens at night.',
      additionalComments: 'Child remained playful between coughing spells but had a rough night of sleep.',
      symptoms: [
        { type: 'Cough', severity: 'Moderate', severityCode: 2, duration: '' },
        { type: 'CoughDry', severity: 'Moderate', severityCode: 2, duration: '' },
        { type: 'RapidBreathing', severity: 'Moderate', severityCode: 2, duration: '' },
        { type: 'NoisyBreathing', severity: 'Mild', severityCode: 1, duration: '' },
        { type: 'NoisyBreathingBoth', severity: 'Mild', severityCode: 1, duration: '' },
        { type: 'NasalCongestion', severity: 'Mild', severityCode: 1, duration: '' },
        { type: 'MucusClearWhite', severity: 'Mild', severityCode: 1, duration: '' },
        { type: 'FeverAxillary', severity: 'Moderate', severityCode: 2, duration: '', temperatureValue: 101.4, temperatureUnit: 'F' },
        { type: 'Cough_F1', severity: 'Moderate', severityCode: 2, duration: '' },
        { type: 'CoughDry_F1', severity: 'Mild', severityCode: 1, duration: '' },
        { type: 'RapidBreathing_F1', severity: 'Mild', severityCode: 1, duration: '' },
        { type: 'NoisyBreathing_F1', severity: 'Moderate', severityCode: 2, duration: '' },
        { type: 'NoisyBreathingBoth_F1', severity: 'Mild', severityCode: 1, duration: '' },
        { type: 'NasalCongestion_F1', severity: 'NoChange', severityCode: 6, duration: '' },
        { type: 'Fever_F1', severity: 'NoChange', severityCode: 6, duration: '', temperatureValue: 100.3, temperatureUnit: 'F' },
        { type: 'Cough_F2', severity: 'Resolved', severityCode: 4, duration: '' },
        { type: 'RapidBreathing_F2', severity: 'Resolved', severityCode: 4, duration: '' },
        { type: 'NoisyBreathing_F2', severity: 'MuchBetter', severityCode: 3, duration: '' },
        { type: 'NasalCongestion_F2', severity: 'LittleBetter', severityCode: 2, duration: '' },
        { type: 'MucusYellowGreen_F2', severity: 'Moderate', severityCode: 2, duration: '' },
      ],
      medications: [
        { name: 'Albuterol', dosage: '2 puffs', time: '8:10 AM' },
        { name: 'Acetaminophen', dosage: '7.5 mL', time: '8:40 AM' },
      ],
      exposures: [
        { type: 'Flu/Cold/COVID/RSV' },
        { type: 'Sick contacts', description: 'Another close contact' },
        { type: 'Other', description: 'Dust from renovation' },
      ],
      activities: [
        { type: 'Sleep', impact: 'Interrupted', description: 'Cough overnight with frequent waking' },
      ],
      notes: 'Demo notes'
    },
    reportPdfUrl: 'https://mozilla.github.io/pdf.js/web/compressed.tracemonkey-pldi-09.pdf',
    videos: [
      {
        id: 'v1',
        type: 'symptoms',
        title: 'Starting Symptoms',
        phase: 'initial',
        thumbnailUrl: `${assetHost}/asset/mamabearicon.png`,
        videoUrl: `${demoVideoBase}?clip=initial`,
        duration: '0:15',
      },
      {
        id: 'v2',
        type: 'medication',
        title: 'Follow-up 1',
        phase: 'followup1',
        thumbnailUrl: `${assetHost}/asset/mamabearicon.png`,
        videoUrl: `${demoVideoBase}?clip=followup1`,
        duration: '0:15',
      },
      {
        id: 'v3',
        type: 'symptoms',
        title: 'Follow-up 2',
        phase: 'followup2',
        thumbnailUrl: `${assetHost}/asset/mamabearicon.png`,
        videoUrl: `${demoVideoBase}?clip=followup2`,
        duration: '0:15',
      },
    ],
    expirationDate: undefined,
    accessCount: 1,
  };

  if (singleReportPreview) {
    mock.chainData = [mock.checkInData];
  }

  // Bypass API layer for demo by injecting shareId token
  sessionStorage.setItem('shareToken', mock.shareId);
  return <ReportViewer shareId={mock.shareId} mockData={mock} bypassApi viewerType={viewerType} accessCode={demoAccessCode} />;
};

const SiteFooter: React.FC = () => (
  <footer className="report-footer">
    <div className="report-frame flex flex-col items-center gap-3">
      <img
        src="/asset/loonduck_custom.png"
        alt="MamaBear Health loon"
        className="h-24 w-auto"
      />
      {/* Previous footer branding retained in case the PI asks to restore it.
      <img src="/asset/loonduck.png" alt="MamaBear Health" className="h-16 w-auto" />
      <p className="inline-flex items-center justify-center space-x-2 text-sm">
        <span>MamaBear Health is a product of</span>
        <a className="report-copy-link" href="https://loonmd.com/" target="_blank" rel="noreferrer">
          <span className="font-medium">LoonMD</span>
        </a>
      </p>
      */}
      <a className="report-copy-link text-sm font-medium" href="http://www.mamabearhealth.com/" target="_blank" rel="noreferrer">
        www.mamabearhealth.com
      </a>
    </div>
  </footer>
);
